import type { QuestionVisibilityState } from "@/components/survey/Question";
import {
  DiabetesTypeOptionId,
  type DisqualificationReason,
  PrimaryCareProviderSelection,
} from "@/constants";

export const NOOM_DIABETES_ELIGIBLE_DIABETES_TYPES = [
  DiabetesTypeOptionId.TYPE2,
  DiabetesTypeOptionId.UNSURE,
];

/**
 * This function returns whether a user qualifies for Noom Diabetes based on
 * the current visibility state.
 *
 * A user qualfies for Noom Diabetes if all of the following are true:
 * - has Type 2 or unsure diabetes
 *   - which means the user has to have answered that they have diabetes
 * - has a primary care provider
 *
 * This is used to determine question visibility so it does mean it will return
 * null if some questions are not answered
 *
 * @param {QuestionVisibilityState} state The entire visibility state
 * @returns {DisqualificationReason | null} Returns a disqualification reason
 * if the user does not qualify for Noom Diabetes, null if the user qualifies
 */
export const qualifiesForNoomDiabetes = ({
  eligibilityInfo,
  retriageEligibility,
  surveyAnswers,
}: QuestionVisibilityState): DisqualificationReason | null => {
  const { diabetesDiagnosis, diabetesType, primaryCareProvider } =
    surveyAnswers;
  const { eligiblePrograms = [] } = eligibilityInfo || {};
  const { eligibleRetriagePrograms = [] } = retriageEligibility || {};

  if (
    !eligiblePrograms.includes("DIABETES_LIFESTYLE") &&
    !eligibleRetriagePrograms.includes("DIABETES_LIFESTYLE")
  ) {
    return "PROGRAM_NOT_OFFERED";
  }

  /**
   * If the user doesn't have diabetes, diabetesType will always be undefined
   * since that question will be skipped so this check is in place to ensure
   * the user ends up answering the diabetes type question.
   */
  if (diabetesDiagnosis === "no") {
    return "NO_DIABETES_DIAGNOSIS";
  }

  if (
    diabetesType &&
    !NOOM_DIABETES_ELIGIBLE_DIABETES_TYPES.includes(diabetesType)
  ) {
    return "INELIGIBLE_DIABETES_TYPE";
  }

  if (primaryCareProvider === PrimaryCareProviderSelection.NO) {
    return "NO_PRIMARY_CARE_PROVIDER";
  }

  return null;
};
